import { memo, useMemo, useState } from "react";
import Accordion from "../accordian";
import { useTranslation } from "react-i18next";
import {
  Controller,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import { useGetAllHosts } from "../../hooks/common/query";
import { formattedDateTime, mapToOptions } from "../../lib/functions";
import toast from "react-hot-toast";
import TextInput from "../inputs/TextInput";
import DateTimePickerInput from "../inputs/DateTimePickerInput";
import { APP_ROUTES } from "../../routes";
import { useLocation } from "react-router-dom";

interface PCInOutPermitDetailsCardProps {
  control: any;
  errors: any;
  clearErrors: UseFormClearErrors<{
    employeeName: string;
    uploadEmployeeIDCard?: any;
    deviceType: string;
    serialNumber: string;
    building: string;
    permitStartDate: string;
    permitEndDate: string;
  }>;
  setValue: UseFormSetValue<{
    employeeName: string;
    uploadEmployeeIDCard?: any;
    deviceType: string;
    serialNumber: string;
    building: string;
    permitStartDate: string;
    permitEndDate: string;
  }>;

  permitStartDate: Date;

  isFieldsDisabled?: boolean;
}

const PCInOutPermitDetailsCard = ({
  control,
  errors,
  clearErrors,
  setValue,
  permitStartDate,
  isFieldsDisabled,
}: PCInOutPermitDetailsCardProps) => {
  const { t } = useTranslation();

  const location = useLocation();

  const [selectedKey, setSelectedKey] = useState<string>("1");

  const { data: hosts } = useGetAllHosts();

  const hostNames = useMemo(
    () => mapToOptions("fullName", "fullName", "user_id", hosts),
    [hosts],
  );

  return (
    <div className="bg-white z-10 p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2 items-center">
            {t("1")}. {t("permitDetails")}
          </h1>
        }
      >
        <div className="flex flex-col gap-2 lg:gap-4">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
            <Controller
              name="employeeName"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={hostNames}
                  value={
                    hostNames?.find((item) => item.value === Number(value))
                      ?.labelEn || ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("employeeName")}
                  required
                  readOnly={
                    isFieldsDisabled
                    // user?.n_RoleID !== USER_ROLES_IDS.ADMIN ||
                    // (user?.n_RoleID === USER_ROLES_IDS.ADMIN &&
                    //   location.pathname ===
                    //     APP_ROUTES.APPROVE_PC_IN_OUT_PERMIT)
                  }
                />
              )}
            />

            <Controller
              name="deviceType"
              control={control}
              render={({ field: { onChange, name, value } }) => {
                return (
                  <TextInput
                    label={t("deviceType")}
                    value={value || ""}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required
                    readOnly={isFieldsDisabled}
                  />
                );
              }}
            />

            <Controller
              name="serialNumber"
              control={control}
              render={({ field: { onChange, name, value } }) => {
                return (
                  <TextInput
                    label={t("serialNumber")}
                    value={value || ""}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required
                    readOnly={isFieldsDisabled}
                  />
                );
              }}
            />

            <Controller
              name="building"
              control={control}
              render={({ field: { onChange, name, value } }) => {
                return (
                  <TextInput
                    label={t("building")}
                    value={value || ""}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required
                    readOnly={isFieldsDisabled}
                  />
                );
              }}
            />

            <Controller
              name="permitStartDate"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <DateTimePickerInput
                  label={t("permitStartDate")}
                  value={value}
                  required
                  readOnly={isFieldsDisabled}
                  onChange={(value) => {
                    if (!value) return;

                    onChange(value);
                    clearErrors(name);

                    // Set permitEndDate to permitStartDate + 1 hour
                    const newEndDate = new Date(value);
                    newEndDate.setHours(newEndDate.getHours() + 1);
                    setValue("permitEndDate", formattedDateTime(newEndDate));
                  }}
                  error={errors?.[name]?.message}
                  minDate={
                    location.pathname !== APP_ROUTES.UPDATE_PC_IN_OUT_PERMIT
                      ? new Date()
                      : undefined
                  }
                  // readOnly={isReadonly}
                  dateTimeValidation={(newDate: Date) => {
                    if (
                      // location.pathname !== APP_ROUTES.APPROVE_REQUEST &&
                      // location.pathname !== APP_ROUTES.UPDATE_REQUEST &&
                      newDate < new Date()
                    ) {
                      toast.error(t("permitStartTimeCannotBeInPast"));
                      return false;
                    }
                    return true;
                  }}
                />
              )}
            />

            <Controller
              name="permitEndDate"
              control={control}
              render={({ field: { name, value, onChange } }) => {
                //   const currentMaxDate =
                // !userAppointmentDetails ||
                // userAppointmentDetails?.approvalStatus === STATUS_IDS.PENDING ||
                // userAppointmentDetails?.approvalStatus ===
                //   STATUS_IDS.REQUEST_INFO
                //   ?
                // maxEndDate;
                //   : undefined;

                return (
                  <DateTimePickerInput
                    label={t("permitEndDate")}
                    value={value}
                    required
                    readOnly={isFieldsDisabled}
                    onChange={(value) => {
                      onChange(value);

                      // Ensure the selected value doesn't exceed maxDate
                      // if (
                      //   currentMaxDate &&
                      //   value &&
                      //   new Date(value) > currentMaxDate
                      // ) {
                      //   onChange(currentMaxDate);
                      // } else {
                      //   onChange(value);
                      // }
                      clearErrors(name);
                    }}
                    error={errors?.[name]?.message}
                    minDate={new Date()}
                    dateTimeValidation={(newDate: Date) => {
                      if (newDate < permitStartDate) {
                        toast.error(t("permitEndTimeGreaterThanStartTime"));
                        return false;
                      }
                      return true;
                    }}
                  />
                );
              }}
            />
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default memo(PCInOutPermitDetailsCard);
